/**
 * Global metric controls — working-day basis and the stop clock.
 *
 * These are not decoration: the same figure under a calendar-day basis and a
 * working-day basis is a different number, and a reader who cannot see which
 * one is in force cannot interrogate it. Both controls are module-wide and
 * every metric request carries them.
 */
import { CalendarClock, TimerOff } from 'lucide-react';
import { useIptPrefs, useIptTheme } from './iptTheme';
import { Def } from './IptPrimitives';

export const IptPrefsBar = ({ compact = false }) => {
  const T = useIptTheme();
  const { dayBasis, stopClock, set } = useIptPrefs();

  const Toggle = ({ on, onClick, icon: Icon, label, testId, def }) => (
    <span className="inline-flex items-center gap-1">
      <button type="button" onClick={onClick} data-testid={testId}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-semibold transition-colors"
        style={on
          ? { background: T.tint(T.primary, '1F'), color: T.primary,
              border: `1px solid ${T.tint(T.primary, '55')}` }
          : { background: 'transparent', color: T.muted, border: `1px solid ${T.border}` }}>
        <Icon className="w-3 h-3" /> {label}
      </button>
      <Def T={T} {...def} testId={`${testId}-def`} />
    </span>
  );

  return (
    <div className={`flex items-center gap-2 flex-wrap ${compact ? '' : 'px-0.5'}`}
      data-testid="ipt-prefs-bar">
      {!compact && (
        <span className="text-[9.5px] uppercase tracking-[0.09em]" style={{ color: T.muted }}>
          metric basis
        </span>
      )}
      <Toggle testId="ipt-toggle-day-basis" icon={CalendarClock}
        on={dayBasis === 'working'}
        label={dayBasis === 'working' ? 'working days' : 'calendar days'}
        onClick={() => set({ dayBasis: dayBasis === 'working' ? 'calendar' : 'working' })}
        def={{
          title: 'Day basis',
          body: 'Calendar days count every day. Working days skip weekends and every '
              + 'date in the project holiday calendar, which is client-owned and '
              + 'configurable — nothing is hard-coded into the arithmetic.',
          basis: dayBasis === 'working' ? 'working days' : 'calendar days',
          sample: 'Every figure in the module is labelled with the basis that produced it.',
        }} />
      <Toggle testId="ipt-toggle-stop-clock" icon={TimerOff} on={stopClock}
        label={stopClock ? 'stop clock on' : 'stop clock off'}
        onClick={() => set({ stopClock: !stopClock })}
        def={{
          title: 'Stop clock',
          body: 'With the clock on, periods in a stage the contract suspends — returned '
              + 'to vendor, in dispute — are excluded from the net cycle time. Gross is '
              + 'always reported alongside net, so finance and the contractor read the '
              + 'same number. Suspension is contractual policy and says nothing about '
              + 'fault: the client / vendor split is a separate axis.',
          axis: 'suspension', version: 'suspension-policy/2',
        }} />
    </div>
  );
};

export default IptPrefsBar;
